import {
  createReactComponent,
  IconAlertTriangle,
  IconCircleCheckFilled,
  IconCircleX,
  type TablerIcon,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { StatusTone } from '../../StatusBadge.js';
import { TOKENS } from '../../tokens.js';
import classes from './rest-ui.module.css';

/**
 * Çalışma ve dinlenme ekranlarının ortak parçaları (M6f G3-25, G3-26): iş türü renkleri ve
 * noktası, boş değer, tablo çerçevesi ve bildirim kutusu. Günlük ekran, aylık kayıt ve düzenleme
 * penceresi aynı dili kullanır.
 */

type DayStatus = 'compliant' | 'warning' | 'violation' | 'incomplete';

/** Gün durumunun tonu ve ikonu: günlük, haftalık ve aylık aynı harita (G3-22, G3-38). */
export const DAY_TONE: Readonly<Record<DayStatus, StatusTone>> = {
  compliant: 'success',
  warning: 'warning',
  violation: 'danger',
  incomplete: 'neutral',
};
export const DAY_ICON: Readonly<Record<DayStatus, TablerIcon>> = {
  compliant: IconCircleCheckFilled,
  warning: IconAlertTriangle,
  violation: IconCircleX,
  incomplete: IconAlertTriangle,
};

/** Boş hücre: günlük tablo, pencere ve aylık kayıt aynı işareti kullanır (G3-42). */
export const EMPTY = '--';

/** İş türü renkleri (personel-mlc-calisma-dinlenme.jpeg); renk tek başına anlam taşımaz: ad yanında. */
const ACTIVITY_COLOR: Readonly<Record<string, string>> = {
  watch: TOKENS['primary-600'],
  anchor_watch: TOKENS['activity-anchor'],
  port_operations: TOKENS['activity-port'],
  maneuvering: TOKENS['danger-fg'],
  emergency: TOKENS['danger-fg'],
  cargo: TOKENS['info-fg'],
  maintenance: TOKENS['success-fg'],
  drill: TOKENS['warning-500'],
  // "Others" gri (G3-34); bantta dinlenmeyle karışmasın diye ondan bir ton koyu
  other: `color-mix(in srgb, ${TOKENS['activity-rest']} 60%, ${TOKENS['text-secondary']})`,
  rest: TOKENS['activity-rest'],
};

/** İş türünün rengi; `null` dinlenme. Bilinmeyen tür "Others" gibi gri. */
export function colorOf(activity: string | null): string {
  return ACTIVITY_COLOR[activity ?? 'rest'] ?? (ACTIVITY_COLOR.other as string);
}

/** İş türü noktası (tablo, lejant); ekran okuyucudan gizli, adı yanında yazar. */
export function ActivityDot({ activity }: { activity: string | null }) {
  return <span className={classes.dot} style={{ background: colorOf(activity) }} aria-hidden />;
}

/** Bildirim kutusu: istisna ve uyarı `warning`, hata `danger`, bilgi `info` (G3-24). */
export function RestNotice({
  tone,
  icon,
  children,
  action,
  role,
}: {
  tone: 'info' | 'warning' | 'danger';
  icon: ReactNode;
  children: ReactNode;
  action?: ReactNode;
  role?: 'alert' | 'status';
}) {
  return (
    <div className={classes.notice} data-tone={tone} role={role}>
      <span className={classes.noticeIcon} aria-hidden>
        {icon}
      </span>
      <div className={classes.noticeBody}>{children}</div>
      {action ? <div className={classes.noticeAction}>{action}</div> : null}
    </div>
  );
}

/**
 * Dolu "kişiler" ikonu (personel-mlc-calisma-dinlenme.jpeg "Total Crew"; G3-11): Tabler'de dolu çok
 * kişi ikonu yok. 24×24 ızgarada iki siluet; renk `currentColor`.
 */
export const IconUsersFilled: TablerIcon = createReactComponent(
  'filled',
  'users-filled',
  'UsersFilled',
  [
    ['path', { d: 'M9 4a3.5 3.5 0 1 1 0 7a3.5 3.5 0 0 1 0 -7z', key: 'svg-0' }],
    [
      'path',
      {
        d: 'M7 13h4a5 5 0 0 1 5 5v1.5a1.5 1.5 0 0 1 -1.5 1.5h-11a1.5 1.5 0 0 1 -1.5 -1.5v-1.5a5 5 0 0 1 5 -5z',
        key: 'svg-1',
      },
    ],
    ['path', { d: 'M17 5a3 3 0 1 1 0 6a3 3 0 0 1 0 -6z', key: 'svg-2' }],
    [
      'path',
      {
        d: 'M17.5 13h.5a4 4 0 0 1 4 4v2.5a1.5 1.5 0 0 1 -1.5 1.5h-2.9a3.5 3.5 0 0 0 .4 -1.5v-1.5a6.5 6.5 0 0 0 -2 -4.7c.5 -.2 1 -.3 1.5 -.3z',
        key: 'svg-3',
      },
    ],
  ],
);

/** Ortak tablo sınıfları (CSS modülü): çerçeve, tablo, ikincil hücre, dinlenme satırı. */
export const restTable = classes;
